import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Star, User, ThumbsUp } from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PartnerReviews = ({ partnerId, showTitle = true, limit = 10 }) => {
  const [reviews, setReviews] = useState([]);
  const [ratingSummary, setRatingSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (partnerId) {
      loadReviews();
      loadRatingSummary();
    }
  }, [partnerId]);

  const loadReviews = async () => {
    try {
      const response = await axios.get(`${API}/partners/${partnerId}/reviews?limit=${limit}`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error loading reviews:', error);
      setReviews([]);
    }
  };

  const loadRatingSummary = async () => {
    try {
      const response = await axios.get(`${API}/partners/${partnerId}/rating-summary`);
      setRatingSummary(response.data);
    } catch (error) {
      console.error('Error loading rating summary:', error);
      setRatingSummary(null);
    }
    setLoading(false);
  };

  const renderStars = (rating, size = 'small') => {
    const starSize = size === 'large' ? 'h-6 w-6' : 'h-4 w-4';
    return [...Array(5)].map((_, index) => (
      <Star
        key={index}
        className={`${starSize} ${
          index < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
        }`}
      />
    ));
  };

  const getRatingText = (rating) => {
    const ratingTexts = {
      1: 'Shumë dobët',
      2: 'Dobët', 
      3: 'Mesatar',
      4: 'Mirë',
      5: 'Shkëlqyeshëm'
    };
    return ratingTexts[rating] || '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
        <span className="ml-2 text-gray-600">Duke ngarkuar vlerësimet...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {showTitle && (
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Vlerësimet e Partnerit</h2>
          <p className="text-gray-600">Çfarë thonë klientët tanë</p>
        </div>
      )}

      {/* Rating Summary */}
      {ratingSummary && ratingSummary.total_reviews > 0 && (
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="text-center">
                <div className="text-4xl font-bold text-emerald-600 mb-2">
                  {ratingSummary.average_rating}
                </div>
                <div className="flex justify-center mb-1">
                  {renderStars(Math.round(ratingSummary.average_rating), 'large')}
                </div>
                <div className="text-sm text-gray-600">
                  {ratingSummary.total_reviews} vlerësime
                </div>
              </div>

              <div className="flex-1 ml-8">
                <h3 className="font-semibold mb-3">Shpërndarja e vlerësimeve</h3>
                {[5, 4, 3, 2, 1].map((stars) => (
                  <div key={stars} className="flex items-center mb-2">
                    <span className="text-sm w-8">{stars}</span>
                    <Star className="h-4 w-4 fill-current text-yellow-400 mr-2" />
                    <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                      <div
                        className="bg-emerald-600 h-2 rounded-full"
                        style={{
                          width: `${
                            ratingSummary.total_reviews > 0
                              ? (ratingSummary.rating_distribution[stars] / ratingSummary.total_reviews) * 100
                              : 0
                          }%`
                        }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-600 w-8">
                      {ratingSummary.rating_distribution[stars]}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reviews List */}
      {reviews.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Star className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Nuk ka vlerësime ende.</p>
            <p className="text-sm text-gray-500 mt-2">
              Bëhuni klienti i parë që jep një vlerësim!
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <Card key={review.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                      <User className="h-6 w-6 text-emerald-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{review.customer_name}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex">
                          {renderStars(review.rating)}
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {getRatingText(review.rating)}
                        </Badge>
                        {review.is_verified && (
                          <Badge className="bg-green-100 text-green-800 text-xs">
                            I verifikuar
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-500">
                      {format(new Date(review.review_date), 'dd/MM/yyyy')}
                    </div>
                  </div>
                </div>

                {review.review_text && (
                  <div className="mb-3">
                    <p className="text-gray-700 leading-relaxed">"{review.review_text}"</p>
                  </div>
                )}

                {review.helpful_votes > 0 && (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <ThumbsUp className="h-4 w-4" />
                    <span>{review.helpful_votes} persona e gjetur të dobishme</span>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default PartnerReviews;